import type { DialogueScript, MediaRef, ProjectSnapshot } from '../types';
import { ensureDialogue } from './dialogue';
import { readLocalViewFile } from './localContentStore';
import { api } from './api';

async function hydrateMediaRef(ref: MediaRef | undefined): Promise<MediaRef | undefined> {
  if (!ref || ref.storage !== 'local' || !ref.localKey || ref.blobUrl) return ref;
  const blob = await readLocalViewFile(ref.localKey);
  if (!blob) return ref;
  return { ...ref, blobUrl: URL.createObjectURL(blob) };
}

export async function hydrateDialogueMedia(dialogue: DialogueScript | undefined): Promise<DialogueScript | undefined> {
  if (!dialogue) return undefined;
  const avatars = await Promise.all(dialogue.avatars.map(async (avatar) => ({
    ...avatar,
    image: (await hydrateMediaRef(avatar.image)) ?? avatar.image,
  })));
  const lines = await Promise.all(dialogue.lines.map(async (line) => ({
    ...line,
    voice: await hydrateMediaRef(line.voice),
  })));
  return { avatars, lines, hudStyle: dialogue.hudStyle };
}

async function resolveLocalFile(ref: MediaRef): Promise<File | null> {
  if (ref.storage !== 'local' || !ref.localKey) return null;
  const blob = await readLocalViewFile(ref.localKey);
  if (!blob) return null;
  return new File([blob], ref.name || 'media', { type: blob.type || 'application/octet-stream' });
}

export async function uploadDialogueMedia(project: ProjectSnapshot): Promise<ProjectSnapshot> {
  const dialogue = ensureDialogue(project.dialogue);
  const avatars = await Promise.all(dialogue.avatars.map(async (avatar) => {
    if (avatar.image.storage !== 'local') return avatar;
    const file = await resolveLocalFile(avatar.image);
    if (!file) return avatar;
    const { asset } = await api.uploadMedia(file);
    return {
      ...avatar,
      image: {
        storage: 'r2' as const,
        url: asset.url,
        objectKey: asset.id,
        name: avatar.image.name ?? asset.name,
      },
    };
  }));
  const lines = await Promise.all(dialogue.lines.map(async (line) => {
    if (!line.voice || line.voice.storage !== 'local') return line;
    const file = await resolveLocalFile(line.voice);
    if (!file) return line;
    const { asset } = await api.uploadMedia(file);
    return {
      ...line,
      voice: {
        storage: 'r2' as const,
        url: asset.url,
        objectKey: asset.id,
        name: line.voice.name ?? asset.name,
      },
    };
  }));
  return { ...project, dialogue: { avatars, lines, hudStyle: dialogue.hudStyle }, schemaVersion: 2 };
}
